import { useState, useEffect, useRef } from 'react';
import api from '../../services/api.js';
import Button from '../common/Button.jsx';

export default function RecordingTimeline({ deviceId, deviceName }) {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [total, setTotal]           = useState(0);
  const [playing, setPlaying]       = useState(null);
  const videoRef                    = useRef(null);

  useEffect(() => {
    if (!deviceId) return;
    setLoading(true);
    api.get(`/recordings/device/${deviceId}?page=${page}&limit=12`)
      .then(r => { setRecordings(r.data.data.recordings); setTotal(r.data.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [deviceId, page]);

  const formatDuration = (s) => {
    if (!s) return '—';
    const m = Math.floor(s / 60), sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const formatSize = (b) => {
    if (!b) return '—';
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} Ko`;
    return `${(b / 1024 / 1024).toFixed(1)} Mo`;
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cet enregistrement ?')) return;
    await api.delete(`/recordings/${id}`);
    setRecordings(r => r.filter(x => x.id !== id));
    setTotal(t => t - 1);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600 }}>
            📼 Enregistrements — {deviceName}
          </h3>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)', marginTop: 2 }}>
            {total} enregistrement(s)
          </p>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <div className="spinner" style={{ width: 32, height: 32 }} />
        </div>
      ) : recordings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--elvyra-text-muted)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📼</div>
          <p>Aucun enregistrement disponible</p>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {recordings.map(rec => (
              <div key={rec.id} className="card" style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }}>
                {/* Thumbnail */}
                <div
                  style={{ position: 'relative', aspectRatio: '16/9', background: '#000', cursor: 'pointer' }}
                  onClick={() => setPlaying(rec)}>
                  {rec.thumbnailUrl ? (
                    <img src={rec.thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--elvyra-surface-2)' }}>
                      <span style={{ fontSize: 28, opacity: 0.4 }}>🎬</span>
                    </div>
                  )}
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.3)', opacity: 0, transition: '150ms',
                  }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = 0}>
                    <span style={{ fontSize: 36, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.8))' }}>▶</span>
                  </div>
                  <span style={{
                    position: 'absolute', bottom: 6, right: 6,
                    background: 'rgba(0,0,0,0.7)', borderRadius: 4,
                    fontSize: 10, color: '#fff', padding: '2px 6px', fontWeight: 600,
                  }}>
                    {formatDuration(rec.duration)}
                  </span>
                </div>

                {/* Infos */}
                <div style={{ padding: '8px 10px' }}>
                  <p style={{ fontSize: 11, color: 'var(--elvyra-text-secondary)', marginBottom: 4 }}>
                    {new Date(rec.startedAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 10, color: 'var(--elvyra-text-muted)' }}>{formatSize(rec.size)}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {rec.downloadUrl && (
                        <a href={rec.downloadUrl} download style={{ fontSize: 14, color: 'var(--elvyra-accent)', textDecoration: 'none' }} title="Télécharger">⬇</a>
                      )}
                      <button onClick={() => handleDelete(rec.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--elvyra-danger)' }} title="Supprimer">🗑</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {total > 12 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Précédent</Button>
              <span style={{ display: 'flex', alignItems: 'center', fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)' }}>
                Page {page} / {Math.ceil(total / 12)}
              </span>
              <Button variant="secondary" size="sm" disabled={page >= Math.ceil(total / 12)} onClick={() => setPage(p => p + 1)}>Suivant →</Button>
            </div>
          )}
        </>
      )}

      {/* Lecteur vidéo modal */}
      {playing && (
        <div className="modal-overlay" onClick={() => setPlaying(null)}>
          <div style={{ background: '#000', borderRadius: 'var(--radius-lg)', overflow: 'hidden', maxWidth: 800, width: '100%' }} onClick={e => e.stopPropagation()}>
            <video ref={videoRef} src={playing.downloadUrl} controls autoPlay style={{ width: '100%', display: 'block' }} />
            <div style={{ padding: 'var(--space-3) var(--space-4)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--elvyra-surface)' }}>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--elvyra-text-secondary)' }}>
                {new Date(playing.startedAt).toLocaleString('fr-FR')} · {formatDuration(playing.duration)}
              </span>
              <button onClick={() => setPlaying(null)} style={{ background: 'none', border: 'none', color: 'var(--elvyra-text-muted)', cursor: 'pointer', fontSize: 20 }}>×</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
